import type { ChatRoomMessage } from "@/react/types/Chat/ChatRoomMessage";
import { useChatRoomContext } from "../useChatRoomContext";
import type {
  FetchChatMessagesParams,
  ScrollInfo,
} from "./slices/chatRooms/types";
import type { ChatStoreState } from "./types";
import { useChatStore } from "./useChatStore";

const selectThreads = (state: ChatStoreState) => state.threads;
const selectChatUI = (state: ChatStoreState) => state.ui;
const selectCommunityInbox = (state: ChatStoreState) => state.inbox;
const selectUnreadChatRooms = (state: ChatStoreState) => state.unread;
const selectChatRoomState = (uuid: string) => (state: ChatStoreState) =>
  state.chatRoomStateMap.get(uuid)?.uiState;
const selectChatRoomMessages = (uuid: string) => (state: ChatStoreState) =>
  state.chatRoomStateMap.get(uuid)?.chatRoomMessages;
export const selectChatRooms = (state: ChatStoreState) =>
  Array.from(state.chatRoomMap.values()).reverse();
export const selectChatRoomMessageActions = (state: ChatStoreState) =>
  state.messageActions;
export const selectActions = (state: ChatStoreState) => state.actions;
export const selectChatRoom = (uuid: string) => (state: ChatStoreState) =>
  state.chatRoomMap.get(uuid);
export const selectSendRichTextMessage = (state: ChatStoreState) =>
  state.messageActions.sendRichTextMessage;

export const useThreadsStore = () => useChatStore(selectThreads);
export const useChatUIStore = () => useChatStore(selectChatUI);
export const useCommunityInboxStore = () => useChatStore(selectCommunityInbox);
export const useUnreadChatRoomsStore = () =>
  useChatStore(selectUnreadChatRooms);

export const useChatRoomStoreStates = () => {
  const { createNewChatRoom } = useChatStore(selectActions);
  const { chatRoomUuid } = useChatRoomContext();
  const chatRoomState = useChatStore(selectChatRoomState(chatRoomUuid));
  return chatRoomState ?? createNewChatRoom(chatRoomUuid);
};

export const useChatRoomStoreMessages = () => {
  const { chatRoomUuid } = useChatRoomContext();
  const chatRoomMessages = useChatStore(selectChatRoomMessages(chatRoomUuid));
  return {
    chatRoomMessages,
  };
};

export const useChatRoomStoreChatRooms = () => {
  const chatRooms = useChatStore(selectChatRooms);
  return {
    chatRooms,
  };
};

export const useChatRoomStoreChatRoom = () => {
  const { chatRoomUuid } = useChatRoomContext();
  return useChatStore(selectChatRoom(chatRoomUuid));
};

export const useChatRoomStoreActions = () => {
  const { messageActions } = useChatStore();
  const { chatRoomUuid, isMessaging } = useChatRoomContext();
  return {
    setChatRoomMessages: (messages: ChatRoomMessage[]) =>
      messageActions.setChatRoomMessages(chatRoomUuid, messages),
    fetchChatMessages: (params: FetchChatMessagesParams) =>
      messageActions.fetchChatMessages(chatRoomUuid, isMessaging, params),
    setIsLoading: (isLoading: boolean) =>
      messageActions.setIsLoading(chatRoomUuid, isLoading),
    setIsInitialPageLoaded: (isLoaded: boolean) =>
      messageActions.setIsInitialPageLoaded(chatRoomUuid, isLoaded),
    setHasNextPage: (hasNextPage: boolean) =>
      messageActions.setHasNextPage(chatRoomUuid, hasNextPage),
    setHasPrevPage: (hasPrevPage: boolean) =>
      messageActions.setHasPrevPage(chatRoomUuid, hasPrevPage),
    setScrollInfo: (scrollInfo: ScrollInfo) =>
      messageActions.setScrollInfo(chatRoomUuid, scrollInfo),
    setFirstMessageId: (id: number | null) =>
      messageActions.setFirstMessageId(chatRoomUuid, id),
    setLastMessageId: (id: number | null) =>
      messageActions.setLastMessageId(chatRoomUuid, id),
    setTopMessageId: (id: number | null) =>
      messageActions.setTopMessageId(chatRoomUuid, id),
    setActiveMessageId: (id: string | null) =>
      messageActions.setActiveMessageId(chatRoomUuid, id),
    appendLastMessage: (message: ChatRoomMessage) =>
      messageActions.appendLastMessage(chatRoomUuid, message),
    deleteMessage: (messageId: number) =>
      messageActions.deleteMessage(chatRoomUuid, messageId),
    updateMessage: (message: ChatRoomMessage) =>
      messageActions.updateMessage(chatRoomUuid, message),
  };
};

export const useChatRoomStore = () => ({
  ...useChatRoomStoreStates(),
  ...useChatRoomStoreMessages(),
  ...useChatRoomStoreActions(),
  ...useChatRoomStoreChatRooms(),
});
